import { IkeFieldType } from '@ikegps/core-api';
import { Icon } from '../../components/icon';
import { MaterialIcon } from '../../components/icon/icons';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import type { MithrilVNode } from '../../mithril';
import { m } from '../../mithril';
import { Services } from '../../services';
import { CSS, Style } from './form.palette.style';

addStyles(Style);

export interface FormPaletteOption {
	icon: MaterialIcon;
	title: T;
	maxDepth?: number;
}

export const FIELD_TYPE_DISPLAY: { [k in IkeFieldType]: FormPaletteOption } = {
	[IkeFieldType.TEXT]: { icon: MaterialIcon.FIELD_TEXT, title: T.FIELD_TEXT },
	[IkeFieldType.HEIGHT]: { icon: MaterialIcon.FIELD_HEIGHT, title: T.FIELD_HEIGHT },
	[IkeFieldType.NUMBER]: { icon: MaterialIcon.FIELD_NUMBER, title: T.FIELD_NUMBER },
	[IkeFieldType.TRUE_SIZE]: { icon: MaterialIcon.FIELD_TRUE_SIZE, title: T.FIELD_TRUE_SIZE },
	[IkeFieldType.DATE]: { icon: MaterialIcon.DATE, title: T.FIELD_DATE },
	[IkeFieldType.SELECT_LIST]: { icon: MaterialIcon.FIELD_SELECT_LIST, title: T.FIELD_SELECT_LIST },
	[IkeFieldType.YES_NO]: { icon: MaterialIcon.CHECK_BOX_SELECTED, title: T.FIELD_YES_NO },
	[IkeFieldType.NESTED_LIST]: { icon: MaterialIcon.FIELD_NESTED_LIST, title: T.FIELD_NESTED_LIST },
	[IkeFieldType.FORM]: { icon: MaterialIcon.FIELD_FORM, title: T.FIELD_FORM, maxDepth: 3 },
	[IkeFieldType.LOCATION]: { icon: MaterialIcon.FIELD_LOCATION, title: T.FIELD_LOCATION },
	[IkeFieldType.IMAGE]: { icon: MaterialIcon.FIELD_IMAGE, title: T.FIELD_IMAGE },
	[IkeFieldType.VECTOR]: { icon: MaterialIcon.FIELD_VECTOR, title: T.FIELD_VECTOR },
	[IkeFieldType.ANCHOR]: { icon: MaterialIcon.FIELD_ANCHOR, title: T.FIELD_ANCHOR },
	[IkeFieldType.VERTICAL_SEPARATION]: { icon: MaterialIcon.VERTICAL_SEPARATION, title: T.FIELD_VERTICAL_SEPARATION },
	[IkeFieldType.FILE_TOOL]: { icon: MaterialIcon.CLOUD_UPLOAD, title: T.FIELD_FILE_TOOL },
};

export const FormDesignerPalette = {
	viewPaletteIcon(fieldType: IkeFieldType): MithrilVNode {
		return m('div', { className: `${CSS.field.container} ${CSS.field.container}--${fieldType}` }, [
			this.viewIcon(fieldType),
			this.viewTitle(fieldType),
		]);
	},

	viewInline(fieldType: IkeFieldType): MithrilVNode {
		return m.fragment({}, [this.viewIcon(fieldType), this.viewTitle(fieldType)]);
	},

	viewIcon(fieldType: IkeFieldType, opts?): MithrilVNode {
		return Icon.view(FIELD_TYPE_DISPLAY[fieldType].icon, opts);
	},

	viewTitle(fieldType: IkeFieldType): MithrilVNode {
		return m('div', { className: CSS.field.title }, FormDesignerPalette.translate(fieldType));
	},

	translate(fieldType: IkeFieldType): string {
		return Translate(FIELD_TYPE_DISPLAY[fieldType].title);
	},

	view(onclick: (fieldType: IkeFieldType) => void, depth = 0): MithrilVNode {
		const children: MithrilVNode[] = (Object.keys(FIELD_TYPE_DISPLAY) as (keyof typeof FIELD_TYPE_DISPLAY)[])
			.filter((key) => {
				const option = FIELD_TYPE_DISPLAY[key];

				return Services.User.hasFormPermission(key) && !(option.maxDepth != null && option.maxDepth < depth);
			})
			.map((key) => {
				const item = FormDesignerPalette.viewPaletteIcon(key);

				if (item) {
					item.attrs.onclick = () => {
						onclick(key);
						Services.Analytics.trackEvent(Services.Analytics.EventCategory.Mutate, 'form:palette:add', key);
					};
				}

				return item;
			});

		return m('div', { className: CSS.container }, [
			m('div', { className: CSS.title }, Translate(T.FORM_FIELD_CHOOSE)),
			m('div', { className: CSS.list.container }, children),
		]);
	},
};
